import { type FC } from "react";
import { shallowEqual, useSelector } from "react-redux";

import PageLayout from "../core/navigation-panel/PageLayout.js";
import { AccountSettingsOverviewPage } from "../genome-viewer/AccountSettingsOverviewPage.js";
import AccountSettingsPasswordPage from "./AccountSettingsPasswordPage.js";
import { CreateTokenForm } from "./AccountSettingsRestTokenPage.js";
import PageHeader from "./navigation-panel/PageHeader.js";
import { type IRouteNavItemProps } from "./navigation-panel/RouteNavItem.js";
import { type Capability } from "@/graphql.ts";
import { checkCapability } from "@/modules/users/utils/user.js";
import { AUTHENTICATION_MODE } from "@/redux/modules/users-admin.ts";
import { type IAppState } from "@/redux/reducer.ts";
import { CAPABILITY } from "@/settings/settings.js";

/**
 * Samples Admin Page main component
 */
const AccountSettingsPage: FC = () => {
  const authMode = useSelector<IAppState, string>(
    (state) => state.config.configuration?.authenticationMode,
    shallowEqual,
  );

  const userCapabilities = useSelector<IAppState, Capability[]>(
    (state) => state.auth.session.capabilities,
    shallowEqual,
  );

  const getMenuItems = (): IRouteNavItemProps[] => {
    return [
      {
        label: "Overview",
        route: "overview",
        pageComponent: <AccountSettingsOverviewPage />,
        default: true,
      },
      {
        label: "Change password",
        disabledTooltip:
          "This system is configured to use a directory service, contact your administrator to help changing your password.",
        route: "changePassword",
        disabled: authMode !== AUTHENTICATION_MODE.ORACLE,
        pageComponent: <AccountSettingsPasswordPage />,
      },
      {
        label: "Generate REST API token",
        disabledTooltip:
          "You need permissions to create access tokens for the REST API. Please contact your administrator.",
        route: "restToken",
        disabled: !checkCapability(userCapabilities, CAPABILITY.REST_API_ACCESS),
        // prevent the form to be loaded when entering this page manually by typing the url
        pageComponent: checkCapability(userCapabilities, CAPABILITY.REST_API_ACCESS) && <CreateTokenForm />,
      },
    ];
  };

  return (
    <PageLayout
      navPanelCategories={[
        {
          label: "Account Settings",
          navItems: getMenuItems(),
        },
      ]}
      header={<PageHeader title={"Account Settings"} setDocumentTitle />}
    />
  );
};

export default AccountSettingsPage;
